import { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { EmblaOptionsType } from 'embla-carousel';
import styled from '@emotion/styled';
import { UnstyledButton } from '@myrealtrip/web-ui';
import { IcoChevronRight, IcoChevronLeft } from '@myrealtrip/web-icons';
import { useTheme } from '@emotion/react';

interface Navigation {
  isPrevDisabled: boolean;
  isNextDisabled: boolean;
}

interface NavigationProps {
  hideNav?: boolean;
  heightOffset?: number | string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

interface Props {
  className?: string;
  options?: EmblaOptionsType;
  navOptions?: NavigationProps;
}

const Carousel = ({ children, className, options, navOptions }: PropsWithChildren<Props>) => {
  const { align = 'start', ...restOptions } = options ?? {};
  const {
    hideNav = false,
    heightOffset = '50%',
    onMouseEnter = () => {},
    onMouseLeave = () => {},
  } = navOptions ?? {};
  const { colors } = useTheme();
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align,
    ...restOptions,
  });
  const [navigation, setNavigation] = useState<Navigation>({
    isPrevDisabled: false,
    isNextDisabled: false,
  });

  const handleClickPrev = () => {
    emblaApi?.scrollPrev();
  };

  const handleClickNext = () => {
    emblaApi?.scrollNext();
  };

  const handleScroll = useCallback(() => {
    if (!emblaApi) {
      return;
    }

    setNavigation({
      isPrevDisabled: !emblaApi.canScrollPrev(),
      isNextDisabled: !emblaApi.canScrollNext(),
    });
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) {
      return;
    }

    handleScroll();

    emblaApi.on('scroll', handleScroll);
  }, [emblaApi, handleScroll]);

  return (
    <Container onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <CarouselRoot ref={emblaRef} className={className}>
        <CarouselContainer>{children}</CarouselContainer>
      </CarouselRoot>
      {!hideNav && (
        <>
          {!navigation.isPrevDisabled && (
            <NavigationButton
              direction="prev"
              onClick={handleClickPrev}
              heightOffset={heightOffset}
            >
              <IcoChevronLeft size={24} color={colors.gray.s800} />
            </NavigationButton>
          )}
          {!navigation.isNextDisabled && (
            <NavigationButton
              direction="next"
              onClick={handleClickNext}
              heightOffset={heightOffset}
            >
              <IcoChevronRight size={24} color={colors.gray.s800} />
            </NavigationButton>
          )}
        </>
      )}
    </Container>
  );
};

export default Carousel;

const Container = styled.div`
  position: relative;
`;

const CarouselRoot = styled.div`
  overflow: hidden;
`;

const CarouselContainer = styled.div`
  display: flex;
`;

const NavigationButton = styled(UnstyledButton)<{
  direction: 'prev' | 'next';
  heightOffset: NavigationProps['heightOffset'];
}>`
  position: absolute;
  top: ${({ heightOffset }) =>
    typeof heightOffset === 'string' ? heightOffset : `${heightOffset}px`};
  ${({ direction }) => (direction === 'prev' ? 'left: -24px;' : 'right: -24px;')};
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  padding: 12px;
  color: ${({ theme }) => theme.colors.white};
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 24px;
  box-shadow: 0px 2px 8px 0px rgba(0, 0, 0, 0.15);
`;
